/**
 * Window event fired after this tab changes the multi-product cart (CPS-7479),
 * so cart readers such as the header mini-cart (CPS-7696) and the PDP can
 * revalidate without sharing state with the writer.
 */
export const CART_CHANGED_EVENT = 'compass:cart-changed';

/** The same signal for this site's other tabs (CPS-7696). */
export const CART_CHANGED_CHANNEL = 'compass:cart-changed';

/**
 * What the channel carries: `null` for a cart change, or the org another tab
 * just moved the shared cart to (see `notifyCartOrganizationChanged`).
 */
type CartChannelMessage = { organizationId: string } | null;

let channel: BroadcastChannel | undefined;

// The org this tab reads the cart for, and whether another tab has since
// moved the cart to a different one.
let readerOrganizationId: string | undefined;
let isOrganizationStale = false;
const organizationListeners = new Set<() => void>();

const setOrganizationStale = (isStale: boolean) => {
  if (isOrganizationStale === isStale) return;
  isOrganizationStale = isStale;
  organizationListeners.forEach((listener) => listener());
};

// Every org move decides it: a cart moved away and then back to this tab's
// org is this tab's cart again.
const hearCartOrganization = ({ data }: MessageEvent<CartChannelMessage>) => {
  if (data && readerOrganizationId) {
    setOrganizationStale(data.organizationId !== readerOrganizationId);
  }
};

/**
 * One channel for sending and hearing, so this tab never hears its own
 * changes back. Missing where BroadcastChannel is, and other tabs then catch
 * up on their own focus refetch.
 */
const otherTabs = (): BroadcastChannel | undefined => {
  if (!channel && typeof BroadcastChannel === 'function') {
    channel = new BroadcastChannel(CART_CHANGED_CHANNEL);
    // First, so a reader told of the same message already knows the cart
    // has left its org.
    channel.addEventListener('message', hearCartOrganization);
  }
  return channel;
};

/** The single call site writers use after a successful cart change. */
export const notifyCartChanged = (): void => {
  window.dispatchEvent(new Event(CART_CHANGED_EVENT));
  otherTabs()?.postMessage(null);
};

/**
 * A change only other tabs need: a seat change leaves this tab's count and
 * PDP as they are, but moves the cart version their next write is sent with.
 */
export const notifyOtherTabsCartChanged = (): void => {
  otherTabs()?.postMessage(null);
};

/**
 * commerce-core moved the cart, one per user, to this tab's org as it read
 * it (CPS-7696). A tab still on the old org must not read it again: that
 * read would move it back.
 */
export const notifyCartOrganizationChanged = (organizationId: string): void => {
  readerOrganizationId = organizationId;
  setOrganizationStale(false);
  otherTabs()?.postMessage({ organizationId });
};

/**
 * The org this tab reads the cart for. A switch made in this tab (or a page
 * rendered for another org) is the shopper's own: it takes the cart along.
 */
export const followCartOrganization = (organizationId: string): void => {
  otherTabs();
  if (organizationId === readerOrganizationId) return;
  readerOrganizationId = organizationId;
  setOrganizationStale(false);
};

/**
 * This tab no longer reads an org's cart (signed out, here or in another tab):
 * a guest cart is its own, whatever org a signed-in tab moved theirs to.
 */
export const forgetCartOrganization = (): void => {
  readerOrganizationId = undefined;
  setOrganizationStale(false);
};

/** Another tab moved the cart away from the org this tab reads it for. */
export const isCartOrganizationStale = (): boolean => isOrganizationStale;

/** `useSyncExternalStore` subscribe for `isCartOrganizationStale`. */
export const subscribeToCartOrganizationStale = (
  listener: () => void
): (() => void) => {
  organizationListeners.add(listener);
  return () => {
    organizationListeners.delete(listener);
  };
};

/** Readers subscribe here; returns the unsubscribe for an effect cleanup. */
export const subscribeToCartChanged = (listener: () => void): (() => void) => {
  window.addEventListener(CART_CHANGED_EVENT, listener);
  return () => window.removeEventListener(CART_CHANGED_EVENT, listener);
};

/**
 * Another tab's cart change, its org moves included. A hidden tab hears it
 * once it is shown, however many changes it missed, so background tabs do not
 * each spend a read of the shared rate limit on a badge nobody is looking at.
 * A tab whose cart has moved to another org hears none: its reread would move
 * the cart back.
 */
export const subscribeToOtherTabCartChanged = (
  listener: () => void
): (() => void) => {
  let isPending = false;
  const deliver = () => {
    if (isOrganizationStale) return;
    isPending = document.visibilityState === 'hidden';
    if (!isPending) listener();
  };
  const onVisibilityChange = () => {
    if (isPending) deliver();
  };
  otherTabs()?.addEventListener('message', deliver);
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    otherTabs()?.removeEventListener('message', deliver);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};

/**
 * Window event the PDP fires once its reread after a cart change settles
 * (CPS-7696). That reread runs commerce-core's cart checks, whose tax
 * calculation bumps the cart version, so an open mini-cart rereads too:
 * its next write would otherwise be refused with a 412.
 */
export const CART_REPRICED_EVENT = 'compass:cart-repriced';

export const notifyCartRepriced = (): void => {
  window.dispatchEvent(new Event(CART_REPRICED_EVENT));
};

export const subscribeToCartRepriced = (listener: () => void): (() => void) => {
  window.addEventListener(CART_REPRICED_EVENT, listener);
  return () => window.removeEventListener(CART_REPRICED_EVENT, listener);
};
